import { isBefore, parse } from 'date-fns';
import { action, computed, observable, runInAction } from 'mobx';
import ErrorUtil from '../error/ErrorUtil';
import API from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';

export interface PackageIds {
  single: string;
  group?: string;
}

export interface CampaignInfo {
  id: string;
  customPricePreText?: string;
  singleUserDistributedCampaignPrice?: number;
  multiUserDistributedCampaignPrice?: number;
  packageCustomDistributedCampaignPrices?: number[];
  rulesLink?: string;
  rulesLinkOnlyInModal?: boolean;
  tooltipText?: string;
  endDate?: string;
}

interface PackagePrices {
  id: string;
  price: number;
  priceWithoutDiscount?: number;
  offerInfo?: PackageDiscountOfferInfo;
}

interface PackageDiscountOfferInfo {
  offerStart: string;
  offerEnd: string;
  offerName: string;
}

export const CALLS_PACKAGE_ID: string = '16172';

class MobileLifeStore {
  @observable accessor orderModalOpen = false;
  @observable accessor selectedMobilePackage: string;
  @observable accessor selectedDeviceCount: number = 1;
  @observable accessor pricesFetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor prices: Map<string, PackagePrices> = new Map();
  @observable accessor showOnlyCallPackages: boolean = false;

  public getPackagePrices = (packageId: string): PackagePrices => {
    return this.prices.get(packageId);
  };

  @computed
  public get isPricesFetched(): boolean {
    return this.pricesFetchStatus !== FetchStatus.FETCHING;
  }

  public hasPackageDiscount = (packageId: string): boolean => {
    const prices = this.getPackagePrices(packageId);
    if (!prices?.offerInfo) {
      return false;
    }
    const { offerStart, offerEnd } = prices.offerInfo;
    const startDate = parse(offerStart, 'yyyy-MM-dd', Date.now());
    const endDate = parse(offerEnd, 'yyyy-MM-dd', Date.now());
    endDate.setHours(23, 59);
    return (!offerStart || isBefore(startDate, Date.now())) && (!offerEnd || isBefore(Date.now(), endDate)) && !!prices.priceWithoutDiscount;
  };

  @action.bound
  public resetPackagePrices() {
    this.prices = new Map();
  }

  public fetchPackagePrices = async (singlePackageIds: string[], groupPackageIds: string[]) => {
    if (!singlePackageIds.length && !groupPackageIds.length) {
      return Promise.resolve();
    }
    try {
      runInAction(() => {
        this.pricesFetchStatus = FetchStatus.FETCHING;
      });
      const singlePackagePricesPromise = this.getPackagePricesPromise([...singlePackageIds, CALLS_PACKAGE_ID], true);
      const groupPackagePricesPromise = this.getPackagePricesPromise(groupPackageIds, false);
      const [singlePackagePrices, groupPackagePrices] = await Promise.all([singlePackagePricesPromise, groupPackagePricesPromise]);
      this.setPackagePricesAndSuccessStatus([...singlePackagePrices.data, ...groupPackagePrices.data]);
    } catch (e) {
      runInAction(() => {
        this.pricesFetchStatus = FetchStatus.ERROR;
        ErrorUtil.pushErrorToTeliaRumAndLog(e);
      });
    }
  };

  @action.bound
  public setOrderModalOpen(value: boolean) {
    this.orderModalOpen = value;
  }

  @action.bound
  public setSelectedMobilePackage(mobilePackage: string) {
    this.selectedMobilePackage = mobilePackage;
  }

  @action.bound
  public setSelectedDeviceCount(value: number, selectedPackageIds: PackageIds) {
    this.selectedDeviceCount = value;
    this.selectedMobilePackage = this.getPackageId(selectedPackageIds);
  }

  @action.bound
  public setShowOnlyCallPackages(value: boolean) {
    this.showOnlyCallPackages = value;
  }

  public getPackageId = (packageIds: PackageIds) => {
    return this.selectedDeviceCount > 1 ? packageIds.group : packageIds.single;
  };

  private getPackagePricesPromise = (packageIds: string[], isSingle: boolean) => {
    return API.get<PackagePrices[]>('/mobile/package/prices', { params: { packageIds, isSingle } });
  };

  private setPackagePricesAndSuccessStatus = (priceInfos: PackagePrices[]) => {
    runInAction(() => {
      priceInfos.forEach((info) => {
        this.prices.set(info.id, info);
      });
      this.pricesFetchStatus = FetchStatus.FETCHED;
    });
  };
}

export default MobileLifeStore;
